import { config } from "../models/config";
import { authHeaders, jsonHeaders } from "./apiHeaders";
import { ApiError, type GetToken } from "./accountApi";
import type {
  DesktopApp,
  DesktopPlatform,
  DesktopRelease,
  RegisterAppRequest,
  UpdateAppRequest,
} from "../models/downloads";

// Typed client for the 다운로드 centre. Same bearer-fetch pattern as accountApi/eqmsApi: reads are on
// /api/downloads (any signed-in user), writes on /api/admin/downloads (platform admin only — the
// backend's security chain, not this file, is what enforces that).

async function call<T>(getToken: GetToken, method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    method,
    headers: jsonHeaders(getToken),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json().catch(() => undefined as T);
}

const enc = encodeURIComponent;

// ---- Reads ----

export const listDesktopApps = (t: GetToken) =>
  call<DesktopApp[]>(t, "GET", "/api/downloads/apps");

export const getDesktopApp = (t: GetToken, id: string) =>
  call<DesktopApp>(t, "GET", `/api/downloads/apps/${enc(id)}`);

/** The bearer-authed icon URL. Fetched as a blob (an <img src> cannot carry the token). */
export async function fetchIconUrl(getToken: GetToken, appId: string): Promise<string> {
  const res = await fetch(`${config.apiBase}/api/downloads/apps/${enc(appId)}/icon`, {
    headers: authHeaders(getToken),
  });
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  return URL.createObjectURL(await res.blob());
}

/**
 * Downloads an installer through the BFF and hands it to the browser's save dialog.
 *
 * <p>The response is streamed by the backend but assembled into a Blob here, because the request
 * needs an Authorization header and a plain `<a href>` cannot carry one. Progress is reported per
 * chunk so a hundreds-of-megabytes installer does not look frozen.
 */
export async function downloadRelease(
  getToken: GetToken,
  release: DesktopRelease,
  onProgress?: (receivedBytes: number, totalBytes: number) => void,
): Promise<void> {
  const res = await fetch(`${config.apiBase}/api/downloads/releases/${enc(release.id)}/file`, {
    headers: authHeaders(getToken),
  });
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));

  const total = Number(res.headers.get("content-length") ?? release.size);
  let blob: Blob;
  if (res.body && onProgress) {
    const reader = res.body.getReader();
    const chunks: BlobPart[] = [];
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      onProgress(received, total);
    }
    blob = new Blob(chunks, { type: release.contentType });
  } else {
    blob = await res.blob();
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = release.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Admin writes ----

export const registerDesktopApp = (t: GetToken, req: RegisterAppRequest) =>
  call<DesktopApp>(t, "POST", "/api/admin/downloads/apps", req);

export const updateDesktopApp = (t: GetToken, id: string, req: UpdateAppRequest) =>
  call<DesktopApp>(t, "PATCH", `/api/admin/downloads/apps/${enc(id)}`, req);

export const deleteDesktopApp = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/admin/downloads/apps/${enc(id)}`);

export const deleteDesktopRelease = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/admin/downloads/releases/${enc(id)}`);

async function postFile<T>(
  getToken: GetToken,
  path: string,
  file: File,
  onProgress?: (sentBytes: number, totalBytes: number) => void,
): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  // XHR rather than fetch: only XHR exposes upload progress, and an installer upload without a
  // progress bar is indistinguishable from a hang.
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${config.apiBase}${path}`);
    // XHR has no headers-init, so the shared set is applied one entry at a time.
    for (const [k, v] of Object.entries(authHeaders(getToken))) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      const parsed = (() => {
        try {
          return JSON.parse(xhr.responseText);
        } catch {
          return {};
        }
      })();
      if (xhr.status >= 200 && xhr.status < 300) resolve(parsed as T);
      else reject(new ApiError(xhr.status, parsed));
    };
    xhr.onerror = () => reject(new ApiError(0, { message: "네트워크 오류로 업로드에 실패했습니다." }));
    xhr.send(form);
  });
}

export const uploadDesktopIcon = (t: GetToken, appId: string, file: File) =>
  postFile<DesktopApp>(t, `/api/admin/downloads/apps/${enc(appId)}/icon`, file);

export const publishRelease = (
  t: GetToken,
  appId: string,
  file: File,
  req: { version: string; platform: DesktopPlatform; releaseNotes?: string },
  onProgress?: (sentBytes: number, totalBytes: number) => void,
) =>
  postFile<DesktopRelease>(
    t,
    `/api/admin/downloads/apps/${enc(appId)}/releases?version=${enc(req.version)}`
      + `&platform=${enc(req.platform)}`
      + (req.releaseNotes ? `&releaseNotes=${enc(req.releaseNotes)}` : ""),
    file,
    onProgress,
  );
